import React, { useMemo } from 'react';
import { useAppSelector, useAppDispatch } from 'hooks/redux';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import WarningIcon from '@mui/icons-material/Warning';
import { useTheme } from '@mui/material/styles';
import type { DialogProps } from '@mui/material/Dialog/Dialog';
import ManageHistoryIcon from 'assets/images/addFI/ManageHistory.svg';

import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import InstitutionIcon from 'components/InstitutionIcon';
import { showManageUpdatesDialog } from 'components/Dialogs/ManageUpdatesDialog/dispatcher';

import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import { institutionsSelectors } from 'app-components/flux/institutions';
import { accountsSelectors } from 'app-components/flux/accounts';
import { aggErrors } from 'app-components/flux/account-discovery';
import { rootUiActions } from 'app-components/flux/root-ui';
import { rootUiTypes } from 'app-components/flux/root-ui';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import Paper from '@mui/material/Paper';
import InstitutionItem from 'components/InstitutionItem';
import AccountManageUpdatesChip from 'components/AccountManageUpdatesChip';
import { getLogger } from 'app-components/utils/core';
import { getReauthDialogId } from './dispatcher';

export type ReauthDialogProps = Omit<DialogProps, 'open'> &
  Partial<Pick<DialogProps, 'open'>> & {
    institutionLoginId: string;
  };

const log = getLogger('components/Dialogs/AccountDiscovery/ReauthDialog');

const ReauthDialog = ({ institutionLoginId }: ReauthDialogProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const dialogId = getReauthDialogId(institutionLoginId);

  const closeDialog = () => {
    dispatch(rootUiActions.removeDialog(dialogId));
  };

  const featureFlags = useAppSelector((state) => featureFlagsSelectors.getFeatureFlags(state));

  const institutionLogin = useAppSelector((state) =>
    institutionLoginsSelectors.getInstitutionLoginForId(state, institutionLoginId),
  );
  const institutionId = institutionLogin?.institutionId || '';
  const institution = useAppSelector((state) => institutionsSelectors.getInstitution(state, institutionId));

  const accountsByInstitutionLoginId = useAppSelector(accountsSelectors.getAccountsByInstitutionLoginId);
  const accounts = accountsByInstitutionLoginId?.get(institutionLoginId);

  const accountsText =
    accounts && accounts.size > 1
      ? `${(accounts.first())?.name || ''} & ${accounts.size - 1} other accounts`
      : `${(accounts?.first())?.name || ''}`;

  // Get aggregator status from institution login (not from individual accounts)
  const { aggStatus, aggStatusCode } = useMemo(() => {
    const aggregator = institutionLogin?.aggregators?.get(0);
    const aggStatus = aggregator?.aggStatus;
    const aggStatusCode = aggregator?.aggStatusCode;
    return { aggStatus, aggStatusCode };
  }, [institutionLogin?.aggregators]);

  const aggAction = useMemo(() => {
    const qcsAggAction = aggErrors.getQcsAggregationAction(institutionLogin, aggStatus, aggStatusCode, featureFlags);
    return !(qcsAggAction?.actionWithConfirmation?.payload.type === 'DIALOG_CONFIRMATION')
      ? qcsAggAction
      : {
          ...qcsAggAction,
          get actionWithConfirmation() {
            const content = qcsAggAction.actionWithConfirmation.payload.props.get('content');
            return rootUiActions.createDialog(
              rootUiTypes.mkRootUiData({
                type: 'DIALOG_CONFIRMATION',
                allowNesting: false,
                props: qcsAggAction.actionWithConfirmation.payload.props
                  ?.set(
                    'content',
                    <Box display='flex' flexDirection='column' gap={3}>
                      {typeof content === 'string' ? (
                        <Typography
                          variant='bodyRegular'
                          id='confirmation-dialog-description'
                          sx={{ whiteSpace: 'pre-line' }}
                        >
                          {content}
                        </Typography>
                      ) : (
                        content
                      )}

                      <Paper variant='outlined'>
                        <Box padding={2} gap={2} display='flex' alignItems='center' justifyContent='space-between'>
                          <InstitutionItem value={institutionLogin?.id} />
                          <AccountManageUpdatesChip open institutionLoginId={institutionLogin?.id || ''} />
                        </Box>
                      </Paper>
                    </Box>,
                  )
                  .merge(
                    // replace 'cancel' with 'manage updates' for MFA errors
                    !['MFA_CHALLENGE_READY_FOR_USER', 'MFA_REQUIRED_ACTION'].includes(aggStatus || '')
                      ? {}
                      : {
                          denyButtonVariant: 'text',
                          denyLabel: 'Manage Updates',
                          denyAction: () => showManageUpdatesDialog({ defaultValue: qcsAggAction.institutionLogin.id }),
                          onClose: (_event: Event, reason: string) => reason !== 'DENY',
                        },
                  ),
              }),
            );
          },
        };
  }, [aggStatus, aggStatusCode, featureFlags, institutionLogin]);

  return (
    <StdDialog showCloseButton size='md' title='Re-authorization required' onClose={closeDialog}>
      <StdDialogContent>
        <Box display='flex' alignItems='flex-start' mb={2}>
          <Box position='relative' mr={2}>
            <InstitutionIcon institution={institution} size='md' />
            <Box
              position='absolute'
              bottom={-4}
              right={-4}
              display='flex'
              alignItems='center'
              justifyContent='center'
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                border: `2px solid ${theme.palette.background.paper}`,
              }}
            >
              <WarningIcon fontSize='small' color='warning' />
            </Box>
          </Box>
          <Box flex={1}>
            <Typography variant='bodyRegular' color='textPrimary'>
              {institution?.name || 'Your bank'} is requiring you to re-enter your credentials for account{' '}
              {accountsText}
            </Typography>
          </Box>
        </Box>

        <Box mb={2}>
          <Typography variant='bodyBold' color='textPrimary' mb={1}>
            Some institutions occasionally ask you to re-enter your credentials to stay connected to Quicken.
          </Typography>
          <Box component='ul' m={0} pl='20px'>
            <li>
              <Typography variant='bodyRegular' color='textSecondary'>
                Click <strong>Re-authorize connection</strong> below to sign in with your bank.
              </Typography>
            </li>
            <li>
              <Typography variant='bodyRegular' color='textSecondary'>
                Your accounts will refresh in Quicken after a secure connection is established
              </Typography>
            </li>
          </Box>
        </Box>
      </StdDialogContent>

      <StdDialogActions
        primary={{
          children: 'Re-authorize connection',
          onClick: () => {
            closeDialog();
            if (aggAction) {
              aggErrors.handleAggAction(dispatch, aggAction, 'reauth dialog');
            } else {
              log.error('aggAction is null/undefined!');
            }
          },
          id: 'reauthorize-button',
        }}
        secondary={{
          startIcon: <img src={ManageHistoryIcon} alt='Manage Updates' />,
          children: 'Manage Updates',
          onClick: () => {
            closeDialog();
            showManageUpdatesDialog({ defaultValue: institutionLoginId });
          },
          id: 'manage-updates-button',
        }}
      />
    </StdDialog>
  );
};

export default ReauthDialog;
